import React, { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mhome/ui";
import { useEventEmitter } from "../ws/event-context";
import { useWebSocket } from "../ws/websocket";
import { getRequestBalance } from "../service/billing-service";
import { useRecoilState, useRecoilValue } from "recoil";
import TokenIcon from "@mui/icons-material/Token";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import BugReportIcon from "@mui/icons-material/BugReport";
import SettingsIcon from "@mui/icons-material/Settings";
import { userInfoState, wsReadyState } from "../global-state";
import InstructionInputWrapper from "./instruction-input-wrapper";
import ChatSettingDrawer from "./chat-setting";

const InstructionInput = ({
  placeholder,
  onSendMessage,
  onNewChat,
  onStop,
  onClearHistory,
  isGenerating = false,
}) => {
  const [userInfo] = useRecoilState(userInfoState);
  const [usedBalance, setUsedBalance] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);
  const [debugMode, setDebugMode] = useState(
    userInfo?.setting?.debugMode || false
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { subscribe } = useEventEmitter();
  const { sendAndWaitWs } = useWebSocket();
  const wsReady = useRecoilValue(wsReadyState);

  // Credit handling
  const formatBalance = (v) => {
    const value = (v || 0) / 100;
    return value % 1 === 0 ? value.toString() : value.toFixed(0);
  };

  useEffect(() => {
    const { unsubscribe } = subscribe((target, data) => {
      if (target === "/user/reportBalance") {
        const creditData = JSON.parse(data.payload);
        setUsedBalance(creditData.usedBalance || 0);
      }
    });

    // initial fetch
    if (wsReady) {
      (async () => {
        const creditResp = await getRequestBalance(sendAndWaitWs);
        if (creditResp && !creditResp.error) {
          setUsedBalance(creditResp.usedBalance || 0);
          setTotalBalance(creditResp.totalBalance || 0);
        }
      })();
    }

    return () => unsubscribe();
  }, [wsReady]);

  const handleSendMessage = (instruction, audioFile, mentions) => {
    onSendMessage({
      instruction,
      audioFile: audioFile,
      mentions,
      debugMode,
    });
  };

  const handleNewChat = () => {
    onNewChat({});
  };

  const handleDebugToggle = () => setDebugMode((d) => !d);

  const handleSettingsOpen = () => setSettingsOpen(true);
  const handleSettingsClose = () => setSettingsOpen(false);

  const handleClearHistory = () => {
    if (onClearHistory) {
      onClearHistory();
    }
    setSettingsOpen(false);
  };

  return (
    <div
      style={{ paddingBottom: "8px", paddingLeft: "8px", paddingRight: "8px" }}
    >
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "4px",
          marginBottom: "4px",
          paddingLeft: "4px",
          paddingRight: "4px",
        }}
      >
        <Tooltip title="Used / Total Tokens">
          <CreditBadge>
            <TokenIcon style={{ fontSize: "16px" }} />
            <span style={{ fontSize: "12px", lineHeight: 1 }}>
              {formatBalance(usedBalance)}/{formatBalance(totalBalance)}
            </span>
          </CreditBadge>
        </Tooltip>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tooltip title="Settings">
            <span>
              <IconButton
                onClick={handleSettingsOpen}
                style={{
                  width: "32px",
                  height: "32px",
                  padding: "6px",
                  backgroundColor: "hsl(var(--accent))",
                  borderRadius: "8px",
                }}
                size="small"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
                }}
              >
                <SettingsIcon
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "18px",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          {userInfo?.setting?.debugMode && (
            <Tooltip title="Debug Toggle">
              <span>
                <IconButton
                  onClick={handleDebugToggle}
                  style={{
                    width: "32px",
                    height: "32px",
                    padding: "6px",
                    backgroundColor: debugMode
                      ? "hsl(var(--primary))"
                      : "hsl(var(--accent))",
                    borderRadius: "8px",
                  }}
                  size="small"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = debugMode
                      ? "hsl(var(--primary) / 0.8)"
                      : "hsl(var(--accent))";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = debugMode
                      ? "hsl(var(--primary))"
                      : "hsl(var(--accent))";
                  }}
                >
                  <BugReportIcon
                    style={{
                      color: debugMode
                        ? "hsl(var(--primary-foreground))"
                        : "hsl(var(--muted-foreground))",
                      fontSize: "18px",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="New Chat">
            <span>
              <IconButton
                onClick={handleNewChat}
                style={{
                  width: "32px",
                  height: "32px",
                  padding: "6px",
                  backgroundColor: "hsl(var(--accent))",
                  borderRadius: "8px",
                }}
                size="small"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
                }}
              >
                <AddCommentOutlinedIcon
                  style={{
                    color: "hsl(var(--muted-foreground))",
                    fontSize: "18px",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Actual input component */}
      <InstructionInputWrapper
        placeholder={placeholder}
        onSendMessage={handleSendMessage}
        onStop={onStop}
        isGenerating={isGenerating}
      />

      {/* Settings Drawer */}
      <ChatSettingDrawer
        open={settingsOpen}
        onClose={handleSettingsClose}
        mode="settings"
        onClearHistory={handleClearHistory}
      />
    </div>
  );
};

const CreditBadge = React.forwardRef(({ children }, ref) => {
  return (
    <div
      ref={ref}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        padding: "2px 6px",
        borderRadius: "12px",
        backgroundColor:
          typeof document !== "undefined" &&
          document.documentElement.classList.contains("dark")
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.08)",
        fontSize: "12px",
        color: "hsl(var(--muted-foreground))",
      }}
    >
      {children}
    </div>
  );
});

CreditBadge.displayName = "CreditBadge";

export default InstructionInput;
